// The sound bank: every effect designed once here, rendered offline into a buffer the first time the audio starts,
// then loudness-matched so a shield and a crash sit where the mix wants them whatever their synthesis. Playing a
// sound is then one buffer and two gains: cheap enough for a 50-rider race, and heavier synthesis costs nothing live.
// The stings share one key (D major) so a lap bell, an item chime and the finish fanfare sound like one game.
import { tone, fm, noise, thump, click, metal, saturate, note, type Kit } from './synth.js';

export type BusName = 'sfx' | 'ui' | 'amb';

export interface SoundDef {
  /** Rendered length (s), tail included. */
  len: number;
  /** Target short-term loudness (dBFS, loudest 50 ms window) after normalisation. */
  level: number;
  /** Bus it plays on by default. */
  bus: BusName;
  /** Reverb send (0: dry). */
  verb: number;
  /** How far the riding sounds dip under it (0: not at all). */
  duck?: number;
  /** Random pitch spread per play (fraction): the same hit twice in a row should not sound like a loop. */
  vary: number;
  draw: (k: Kit) => void;
}

const [D5, Fs5, A5, B5, D6, E6, Fs6, A6] = ['D5', 'F#5', 'A5', 'B5', 'D6', 'E6', 'F#6', 'A6'].map(note);
const [D4, Fs4, A4, B4] = ['D4', 'F#4', 'A4', 'B4'].map(note);

const bell = (k: Kit, at: number, f: number, dur: number, vol: number, pan = 0) => fm(k, { at, dur, vol, f, ratio: 2, index: 1.4, pan, detune: 3 });
const chime = (k: Kit, at: number, f: number, dur: number, vol: number, pan = 0) => fm(k, { at, dur, vol, f, ratio: 3.5, index: 0.9, pan });
const pluck = (k: Kit, at: number, f: number, dur: number, vol: number, pan = 0) => tone(k, { at, dur, vol, f, pan, type: 'sawtooth', lp: f * 6, lpTo: f * 1.2, detune: 6, attack: 0.002 });
const sparkle = (k: Kit, at: number, dur: number, vol: number) => {
  noise(k, { at, dur, vol, filter: 'highpass', f: 7000, attack: 0.03, pan: -0.4 });
  noise(k, { at: at + 0.03, dur, vol, filter: 'highpass', f: 8000, attack: 0.03, pan: 0.4 });
};
/** A crowd swell: three formant-like noise bands across the stereo field, rising together. */
const roar = (k: Kit, at: number, dur: number, vol: number, top: number) => {
  noise(k, { at, dur, vol, filter: 'bandpass', f: 450, to: 650, q: 1.1, attack: dur * 0.3, pan: -0.5 });
  noise(k, { at: at + 0.02, dur, vol: vol * 0.9, filter: 'bandpass', f: 1000, to: top, q: 1.3, attack: dur * 0.3, pan: 0.5 });
  noise(k, { at: at + 0.04, dur: dur * 0.8, vol: vol * 0.6, filter: 'bandpass', f: 2400, to: top * 1.4, q: 1.6, attack: dur * 0.25 });
};
/** A hand clap: three micro-bursts a few milliseconds apart, then a short room tail. */
const clap = (k: Kit, at: number, vol: number, pan = 0) => {
  for (const [d, v] of [[0, 1], [0.009, 0.8], [0.019, 0.9]]) noise(k, { at: at + d, dur: 0.012, vol: vol * v, filter: 'bandpass', f: 1300, q: 1.4, attack: 0.0006, pan });
  noise(k, { at: at + 0.026, dur: 0.11, vol: vol * 0.55, filter: 'bandpass', f: 1100, q: 0.9, attack: 0.001, pan });
};

export const SOUNDS: Record<string, SoundDef> = {
  // ---- start: three soft bells, then the gun ----
  beep: { len: 0.6, level: -17, bus: 'sfx', verb: 0.6, vary: 0, draw: k => { bell(k, 0, A5, 0.45, 0.4); tone(k, { at: 0, dur: 0.2, vol: 0.06, f: A6, type: 'sine' }); click(k, 0, 3500, 0.08); } },
  go: {
    len: 1.6, level: -13, bus: 'sfx', verb: 0.8, duck: 0.5, vary: 0, draw: k => {
      [D6, Fs6, A6].forEach((f, i) => bell(k, 0, f, 1.1, 0.22, (i - 1) * 0.35));
      thump(k, 0, 140, 45, 0.35, 0.55);
      noise(k, { at: 0, dur: 0.5, vol: 0.12, filter: 'bandpass', f: 1500, to: 6000, q: 0.8, attack: 0.002 });
      sparkle(k, 0.05, 0.8, 0.05);
    },
  },
  // ---- items ----
  box: { len: 0.5, level: -19, bus: 'sfx', verb: 0.9, vary: 0.02, draw: k => [D6, Fs6, A6, D6 * 2].forEach((f, i) => chime(k, i * 0.04, f, 0.16, 0.18, (i - 1.5) * 0.25)) },
  item: { len: 0.9, level: -16, bus: 'sfx', verb: 1, vary: 0.01, draw: k => { bell(k, 0, A5, 0.25, 0.25, -0.15); bell(k, 0.08, D6, 0.6, 0.3, 0.15); chime(k, 0.08, A6, 0.4, 0.06); sparkle(k, 0.1, 0.4, 0.03); } },
  use: {
    len: 0.5, level: -17, bus: 'sfx', verb: 0.5, vary: 0.05, draw: k => {
      noise(k, { at: 0, dur: 0.3, vol: 0.3, filter: 'bandpass', f: 3500, to: 600, q: 1.6, attack: 0.01 });
      tone(k, { at: 0, dur: 0.2, vol: 0.18, f: 760, to: 280, type: 'triangle' }); click(k, 0, 4000, 0.1);
    },
  },
  drop: {
    len: 0.5, level: -17, bus: 'sfx', verb: 0.35, vary: 0.06, draw: k => {
      thump(k, 0, 240, 70, 0.25, 0.6); noise(k, { at: 0, dur: 0.08, vol: 0.25, f: 900, attack: 0.001 }); click(k, 0, 2200, 0.15);
    },
  },
  fire: {
    len: 0.8, level: -15, bus: 'sfx', verb: 0.6, vary: 0.04, draw: k => {
      const s = saturate(k, 2.2);
      tone(s, { at: 0, dur: 0.35, vol: 0.22, f: 130, to: 880, type: 'sawtooth', lp: 600, lpTo: 5000, q: 4, detune: 12 });
      noise(k, { at: 0, dur: 0.4, vol: 0.28, filter: 'bandpass', f: 600, to: 5500, q: 1.3, attack: 0.005, pan: -0.2 });
      noise(k, { at: 0.12, dur: 0.4, vol: 0.12, filter: 'bandpass', f: 5000, to: 900, q: 1.3, attack: 0.02, pan: 0.3 });
      thump(k, 0, 110, 45, 0.2, 0.4);
    },
  },
  boost: {
    len: 1.2, level: -14, bus: 'sfx', verb: 0.6, duck: 0.35, vary: 0.02, draw: k => {
      const s = saturate(k, 1.8);
      noise(k, { at: 0, dur: 0.9, vol: 0.3, filter: 'bandpass', f: 300, to: 4500, q: 1.1, attack: 0.08, pan: -0.3 });
      noise(k, { at: 0.03, dur: 0.9, vol: 0.25, filter: 'bandpass', f: 380, to: 5200, q: 1.1, attack: 0.08, pan: 0.3 });
      tone(s, { at: 0, dur: 0.8, vol: 0.18, f: 98, to: 392, type: 'sawtooth', lp: 400, lpTo: 3000, detune: 14 });
      thump(k, 0, 70, 120, 0.7, 0.35);
    },
  },
  charge: { len: 0.6, level: -19, bus: 'sfx', verb: 0.7, vary: 0.01, draw: k => [B4, D5, Fs5, B5].forEach((f, i) => pluck(k, i * 0.07, f, 0.18, 0.16, (i - 1.5) * 0.2)) },
  // ---- impacts: the player hit, someone else hit ----
  hurt: {
    len: 1, level: -12, bus: 'sfx', verb: 0.3, duck: 0.6, vary: 0.05, draw: k => {
      const s = saturate(k, 3);
      thump(s, 0, 170, 38, 0.45, 0.8);
      noise(s, { at: 0, dur: 0.4, vol: 0.45, f: 3000, to: 280, attack: 0.001 });
      metal(k, 0.005, 310, 0.5, 0.12, 0.2);
      tone(k, { at: 0, dur: 0.5, vol: 0.12, f: 420, to: 70, type: 'sawtooth', lp: 1400 });
      noise(k, { at: 0.06, dur: 0.35, vol: 0.12, filter: 'highpass', f: 2500, attack: 0.005, pan: -0.4 }); // grit scattering
    },
  },
  hit: {
    len: 0.6, level: -17, bus: 'sfx', verb: 0.45, vary: 0.08, draw: k => {
      const s = saturate(k, 2);
      thump(s, 0, 150, 55, 0.18, 0.6); noise(s, { at: 0, dur: 0.18, vol: 0.35, filter: 'bandpass', f: 1600, to: 450, q: 0.9, attack: 0.001 });
      metal(k, 0, 520, 0.3, 0.07);
    },
  },
  shield: {
    len: 0.9, level: -17, bus: 'sfx', verb: 1.1, vary: 0.03, draw: k => {
      fm(k, { at: 0, dur: 0.6, vol: 0.2, f: 1200, to: 800, ratio: 1.5, index: 1.2, detune: 10 });
      tone(k, { at: 0, dur: 0.4, vol: 0.06, f: 2400, to: 1600, type: 'sine', pan: 0.3 });
      noise(k, { at: 0, dur: 0.25, vol: 0.08, filter: 'highpass', f: 5000, attack: 0.001 });
    },
  },
  warn: {
    len: 0.4, level: -15, bus: 'sfx', verb: 0.2, vary: 0, draw: k => {
      for (const at of [0, 0.15]) tone(k, { at, dur: 0.11, vol: 0.2, f: 1046, type: 'square', lp: 2800, attack: 0.003, detune: 4 });
    },
  },
  storm: {
    len: 1.9, level: -15, bus: 'sfx', verb: 0.8, duck: 0.4, vary: 0.05, draw: k => {
      noise(k, { at: 0, dur: 1.6, vol: 0.4, f: 900, to: 140, attack: 0.04, pan: -0.25 });
      noise(k, { at: 0.03, dur: 1.5, vol: 0.35, f: 800, to: 120, attack: 0.05, pan: 0.25 });
      thump(k, 0.02, 75, 32, 1.2, 0.45);
      for (const [at, p] of [[0.02, -0.5], [0.09, 0.4], [0.17, 0.1]]) noise(k, { at, dur: 0.05, vol: 0.18, filter: 'highpass', f: 3000, attack: 0.001, pan: p }); // crackle
    },
  },
  // ---- race progress ----
  lap: { len: 0.9, level: -16, bus: 'sfx', verb: 0.8, vary: 0, draw: k => { bell(k, 0, A5, 0.25, 0.3); bell(k, 0.12, D6, 0.6, 0.3); chime(k, 0.12, A6, 0.3, 0.05); } },
  finish: {
    len: 2.6, level: -12, bus: 'sfx', verb: 1.2, duck: 0.55, vary: 0, draw: k => {
      [D5, Fs5, A5, D6].forEach((f, i) => pluck(k, i * 0.11, f, 0.25, 0.16, (i - 1.5) * 0.2));
      [A5, D6, Fs6].forEach((f, i) => bell(k, 0.46, f, 1.8, 0.16, (i - 1) * 0.4));
      tone(k, { at: 0.46, dur: 1.6, vol: 0.12, f: D4, type: 'triangle', lp: 900 });
      thump(k, 0.46, 110, 45, 0.4, 0.45);
      sparkle(k, 0.5, 1.4, 0.05);
      roar(k, 0.4, 2, 0.06, 1800);
    },
  },
  lose: { len: 1.3, level: -18, bus: 'sfx', verb: 0.8, vary: 0, draw: k => [A4, Fs4, D4].forEach((f, i) => tone(k, { at: i * 0.2, dur: 0.45, vol: 0.2, f, type: 'triangle', lp: 1400 })) },
  elim: { len: 0.5, level: -19, bus: 'sfx', verb: 0.6, vary: 0.03, draw: k => tone(k, { at: 0, dur: 0.32, vol: 0.2, f: 300, to: 150, type: 'triangle', lp: 1500 }) },
  deny: {
    len: 0.35, level: -18, bus: 'sfx', verb: 0.1, vary: 0, draw: k => {
      tone(k, { at: 0, dur: 0.09, vol: 0.2, f: 155, type: 'square', lp: 1000 }); tone(k, { at: 0.11, dur: 0.12, vol: 0.2, f: 146, type: 'square', lp: 1000 });
    },
  },
  // ---- workouts ----
  interval: { len: 0.9, level: -15, bus: 'sfx', verb: 0.7, duck: 0.3, vary: 0, draw: k => { chime(k, 0, A5, 0.25, 0.3); chime(k, 0.14, E6, 0.6, 0.3); tone(k, { at: 0.14, dur: 0.3, vol: 0.04, f: E6 * 2, type: 'sine' }); } },
  tick: { len: 0.12, level: -22, bus: 'sfx', verb: 0.2, vary: 0, draw: k => { tone(k, { at: 0, dur: 0.05, vol: 0.3, f: 1500, type: 'sine', attack: 0.001 }); click(k, 0, 5000, 0.06); } },
  // ---- end screen and interface ----
  levelup: {
    len: 2.4, level: -14, bus: 'ui', verb: 1.2, vary: 0, draw: k => {
      [D5, Fs5, A5, D6, Fs6, A6].forEach((f, i) => bell(k, i * 0.08, f, 0.5, 0.14, (i - 2.5) * 0.15));
      [D6, Fs6, A6].forEach((f, i) => bell(k, 0.5, f, 1.6, 0.12, (i - 1) * 0.4));
      tone(k, { at: 0.5, dur: 1.4, vol: 0.1, f: D4, type: 'triangle', lp: 800 });
      sparkle(k, 0.45, 1.2, 0.05);
    },
  },
  // FTP up: a climb in fourths and fifths, a bright bell on top (lighter than the level-up)
  ftpup: {
    len: 1.8, level: -15, bus: 'ui', verb: 1.1, vary: 0, draw: k => {
      [A4, D5, Fs5, A5, D6].forEach((f, i) => pluck(k, i * 0.07, f, 0.3, 0.14, (i - 2) * 0.2));
      chime(k, 0.38, A6, 1, 0.12); sparkle(k, 0.35, 0.8, 0.04);
    },
  },
  click: { len: 0.06, level: -24, bus: 'ui', verb: 0, vary: 0.04, draw: k => { click(k, 0, 4500, 0.3); tone(k, { at: 0, dur: 0.018, vol: 0.15, f: 1800, type: 'sine', attack: 0.0008 }); } },
  // ---- supporters ----
  cheer: {
    len: 1.1, level: -16, bus: 'sfx', verb: 0.9, vary: 0.04, draw: k => {
      roar(k, 0, 0.8, 0.16, 1700); clap(k, 0.1, 0.25, -0.4); clap(k, 0.24, 0.22, 0.5);
      bell(k, 0.05, A5, 0.25, 0.1); bell(k, 0.17, D6, 0.35, 0.1);
    },
  },
  cheerBoost: {
    len: 1.5, level: -14, bus: 'sfx', verb: 1, vary: 0.03, draw: k => {
      roar(k, 0, 1.1, 0.18, 2200); [0.08, 0.2, 0.31, 0.45].forEach((at, i) => clap(k, at, 0.22, i % 2 ? 0.5 : -0.5));
      [A5, D6, Fs6].forEach((f, i) => bell(k, i * 0.1, f, 0.4, 0.1)); chime(k, 0.3, A6, 0.5, 0.06);
    },
  },
  // ---- bike ----
  // derailleur: a sharp metallic click, the cage moving, then the chain settling on the next sprocket
  shift: {
    len: 0.18, level: -20, bus: 'sfx', verb: 0.1, vary: 0.06, draw: k => {
      noise(k, { at: 0, dur: 0.025, vol: 0.3, filter: 'bandpass', f: 3800, q: 3, attack: 0.0008 });
      metal(k, 0.002, 1900, 0.06, 0.05);
      noise(k, { at: 0.045, dur: 0.05, vol: 0.2, filter: 'bandpass', f: 2400, q: 2, attack: 0.001 });
      thump(k, 0.045, 300, 120, 0.04, 0.12);
    },
  },
  // ---- roadside crowd one-shots (riding sounds, placed by audio/ride.ts) ----
  clap: { len: 0.2, level: -24, bus: 'amb', verb: 0.6, vary: 0.08, draw: k => clap(k, 0, 0.4) },
  // cowbells on the climbs: the classic two square tones through a resonant band-pass
  cowbell: {
    len: 0.45, level: -24, bus: 'amb', verb: 0.7, vary: 0.05, draw: k => {
      for (const at of [0, 0.16]) for (const f of [560, 845]) tone(k, { at, dur: 0.22, vol: 0.12, f, type: 'square', lp: 2600, q: 3, attack: 0.001 });
    },
  },
  whistle: {
    len: 0.8, level: -26, bus: 'amb', verb: 0.8, vary: 0.06, draw: k => {
      tone(k, { at: 0, dur: 0.25, vol: 0.18, f: 2200, to: 3100, type: 'sine', attack: 0.02 });
      tone(k, { at: 0.25, dur: 0.45, vol: 0.18, f: 3100, to: 2000, type: 'sine', attack: 0.01 });
    },
  },
};

export const SOUND_NAMES = Object.keys(SOUNDS);

/** Loudest 50 ms window (RMS over both channels) and the sample peak of a rendered sound. */
export function measure(chans: Float32Array[], sampleRate: number): { rms: number; peak: number } {
  const win = Math.max(1, Math.floor(sampleRate * 0.05)), n = chans[0]?.length ?? 0;
  let peak = 0, best = 0;
  for (let s = 0; s < n; s += win >> 1) {
    let sum = 0, cnt = 0;
    for (const c of chans) for (let i = s; i < Math.min(n, s + win); i++) { const x = c[i]; sum += x * x; cnt++; if (Math.abs(x) > peak) peak = Math.abs(x); }
    if (cnt) best = Math.max(best, sum / cnt);
  }
  return { rms: Math.sqrt(best), peak };
}

/** Gain bringing a sound to its target loudness, without its peak going over the ceiling (dBFS). Silence stays 0. */
export function normGain(rms: number, peak: number, targetDb: number, ceilingDb = -1): number {
  if (!(rms > 1e-6) || !(peak > 0)) return 0;
  const want = Math.pow(10, targetDb / 20) / rms, cap = Math.pow(10, ceilingDb / 20) / peak;
  return Math.min(want, cap);
}

const ready = new Map<string, AudioBuffer>();
const pending = new Map<string, Promise<AudioBuffer | null>>();

/** Renders one sound offline (stereo) and normalises it in place. */
async function render(name: string, sampleRate: number, noiseBuf: AudioBuffer): Promise<AudioBuffer | null> {
  const def = SOUNDS[name];
  if (!def || typeof OfflineAudioContext === 'undefined') return null;
  const ac = new OfflineAudioContext(2, Math.ceil(def.len * sampleRate), sampleRate);
  def.draw({ ac, out: ac.destination, noise: noiseBuf });
  const buf = await ac.startRendering();
  const chans = [buf.getChannelData(0), buf.getChannelData(1)], m = measure(chans, sampleRate), g = normGain(m.rms, m.peak, def.level);
  for (const c of chans) for (let i = 0; i < c.length; i++) c[i] *= g;
  ready.set(name, buf);
  return buf;
}

/** A sound's buffer if rendered. */
export const bankBuffer = (name: string): AudioBuffer | undefined => ready.get(name);

/** The buffer of a sound, rendering it if needed (shared promise: one render per sound). */
export function loadSound(name: string, sampleRate: number, noiseBuf: AudioBuffer): Promise<AudioBuffer | null> {
  const have = ready.get(name);
  if (have) return Promise.resolve(have);
  let p = pending.get(name);
  if (!p) { p = render(name, sampleRate, noiseBuf).catch(() => null); pending.set(name, p); }
  return p;
}

/** Renders the whole bank, one sound after another so the page never stalls (the countdown's first). */
export async function prepareBank(sampleRate: number, noiseBuf: AudioBuffer) {
  for (const n of ['beep', 'go', ...SOUND_NAMES]) await loadSound(n, sampleRate, noiseBuf);
}
